import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import Button from '../../../shared/components/ui/Button';
import ResponsiveFormLayout from '../../../shared/components/layout/ResponsiveFormLayout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../../../shared/contexts/LanguageContext';

const QR_READER_ID = 'qr-reader-region';

/**
 * Scan / enter the IMA ID card QR (DMIS-unavailable fallback).
 * The QR resolves to https://v.ima.org.iq/{uid}; the doctor can scan it with the camera,
 * paste the URL, or type the number printed under the QR.
 */
const ScanIdScreen = () => {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  const scannerRef = useRef<Html5Qrcode | null>(null);
  const handledRef = useRef(false); // guard against duplicate decodes
  const valueRef = useRef('');

  const authService = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const stopCamera = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try {
        // isScanning may be true; stop() rejects if already stopped — guard it.
        // @ts-ignore - getState exists at runtime
        await scanner.stop();
      } catch {
        /* already stopped */
      }
      try {
        scanner.clear();
      } catch {
        /* noop */
      }
    }
    setScanning(false);
  }, []);

  const handleVerify = useCallback(
    async (raw?: string) => {
      const input = (raw ?? valueRef.current).trim();
      if (!input) {
        setError(t('qr_enter_value'));
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const enteredLicense = Number(sessionStorage.getItem('login_license_number')) || undefined;
        const result = await authService.verifyQr(input, enteredLicense);
        if (!result.isSuccess || !result.data) {
          setError(result.error || t('qr_verify_failed'));
          return;
        }
        const data = result.data;

        if (data.action === 'client_or_manual_qr_fallback') {
          sessionStorage.setItem('qr_url', data.qr_url || '');
          navigate('/qr_manual_fallback');
          return;
        }
        if (data.action === 'otp' && data.reg_num) {
          const regNum = String(data.reg_num);
          sessionStorage.setItem('login_license_number', regNum);
          const sent = await authService.sendOtpByLicense(regNum);
          if (sent.isSuccess) navigate('/confirm_otp');
          else setError(sent.error || t('qr_verify_failed'));
          return;
        }
        if (data.action === 'qr_verified_new' && data.qr_pending_token) {
          sessionStorage.setItem('qr_pending_token', data.qr_pending_token);
          sessionStorage.setItem('qr_prefill', JSON.stringify(data.prefill || {}));
          navigate('/qr_phone_entry');
          return;
        }
        setError(t('qr_verify_failed'));
      } catch (err) {
        setError(err instanceof Error ? err.message : t('qr_verify_failed'));
      } finally {
        setLoading(false);
      }
    },
    [authService, navigate, t]
  );

  // Start the camera scanner once the reader container is mounted (scanning=true).
  useEffect(() => {
    if (!scanning) return;
    handledRef.current = false;
    const scanner = new Html5Qrcode(QR_READER_ID, {
      // Only look for QR codes: makes the per-frame decode far faster and more
      // reliable — especially the ZXing fallback used on iOS Safari, which has
      // no native BarcodeDetector.
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      // Prefer the native BarcodeDetector where available (Android Chrome/desktop).
      useBarCodeDetectorIfSupported: true,
      verbose: false,
    });
    scannerRef.current = scanner;

    const onDecoded = async (decodedText: string) => {
      if (handledRef.current) return;
      handledRef.current = true;
      setValue(decodedText);
      valueRef.current = decodedText;
      await stopCamera();
      handleVerify(decodedText);
    };

    scanner
      .start(
        { facingMode: 'environment' },
        {
          fps: 10,
          // Scan box sized to the (full-screen) viewport for a big, easy target.
          qrbox: (vw: number, vh: number) => {
            const size = Math.floor(Math.min(vw, vh) * 0.7);
            return { width: size, height: size };
          },
          // NOTE: do NOT force an aspectRatio here. A portrait value like
          // innerHeight/innerWidth (~2.1) is one no phone camera can deliver, so
          // the browser returns a distorted stream and the QR never decodes. The
          // full-screen look is done in CSS (the <style> block below) which keeps
          // the video's native aspect ratio, so the scan canvas isn't distorted.
        },
        onDecoded,
        () => {
          /* per-frame decode failures are normal; ignore */
        }
      )
      .catch((err) => {
        setError(err?.message || t('qr_camera_error'));
        scannerRef.current = null;
        setScanning(false);
      });

    return () => {
      // Cleanup on unmount / scanning toggle off.
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        s.stop().catch(() => {}).finally(() => {
          try { s.clear(); } catch { /* noop */ }
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  const toggleCamera = async () => {
    setError(null);
    if (scanning) {
      await stopCamera();
    } else {
      setScanning(true);
    }
  };

  const onManualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    valueRef.current = e.target.value;
    setError(null);
  };

  const reason = sessionStorage.getItem('qr_reason');

  const actions = (
    <Button
      onClick={() => handleVerify()}
      text={t('continue')}
      loading={loading}
      disabled={!value || loading || scanning}
    />
  );

  return (
    <>
      {/* Full-screen camera overlay while scanning */}
      {scanning && (
        <div className="fixed inset-0 z-[9999] bg-black">
          <style>{`
            #${QR_READER_ID}{width:100vw!important;height:100vh!important;display:flex!important;align-items:center!important;justify-content:center!important;overflow:hidden!important;background:#000;}
            /* Fill the screen HEIGHT but keep the camera's natural aspect ratio
               (width:auto). Forcing BOTH width:100vw and height:100vh gave the
               <video> a portrait box while the camera frame is landscape, so
               html5-qrcode mapped the scan canvas with mismatched x/y scale
               factors and handed a horizontally-squashed image to the decoder —
               fine for the robust desktop BarcodeDetector, but unreadable for the
               WASM decoder iOS Safari must use. Matching the box aspect to the
               frame keeps the scanned image undistorted (fills height, crops sides). */
            #${QR_READER_ID} video{height:100vh!important;width:auto!important;max-width:none!important;}
          `}</style>
          <div id={QR_READER_ID} className="w-screen h-screen" />
          {/* Close button */}
          <button
            type="button"
            onClick={toggleCamera}
            className="absolute top-5 right-5 z-10 w-12 h-12 flex items-center justify-center bg-black/50 text-white rounded-full text-2xl"
            aria-label={t('qr_close_camera')}
          >
            ✕
          </button>
          {/* Hint */}
          <div className="absolute bottom-10 left-0 right-0 px-6 text-center text-white text-sm">
            {t('qr_point_camera')}
          </div>
          {error && (
            <div className="absolute bottom-20 left-0 right-0 px-6 text-center text-red-300 text-sm">{error}</div>
          )}
        </div>
      )}

    <ResponsiveFormLayout
      showBackButton={true}
      onBack={async () => { await stopCamera(); navigate('/login'); }}
      showLanguageToggle={true}
      icon="/ima_logo.png"
      showIcon={true}
      iconSize="w-40 h-32"
      title={t('qr_scan_title')}
      subtitle={reason === 'expired' ? t('qr_scan_subtitle_expired') : t('qr_scan_subtitle')}
      actions={actions}
      maxHeight="95vh"
    >
      <div className="w-full">
        <div className="text-center mb-3 text-[#726c6c] text-sm leading-relaxed">
          {t('qr_scan_instruction')}
        </div>

        {/* Camera scanner */}
        <button
          type="button"
          onClick={toggleCamera}
          disabled={loading}
          className="w-full mb-3 px-4 py-3 rounded-2xl border-2 border-[#2e5d7d] text-[#2e5d7d] font-semibold flex items-center justify-center gap-2"
        >
          <span aria-hidden>📷</span>
          {t('qr_open_camera')}
        </button>

        {/* Manual entry fallback */}
        {!scanning && (
          <>
            <div className="text-center text-xs text-[#a0a0a0] my-2">{t('qr_or_paste')}</div>
            <input
              type="text"
              value={value}
              onChange={onManualChange}
              placeholder={t('qr_input_placeholder')}
              className={`w-full px-4 py-3 text-base border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#2e5d7d] ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              dir="ltr"
            />
          </>
        )}

        {error && !scanning && <div className="text-red-500 text-sm mt-2 text-center">{error}</div>}
      </div>
    </ResponsiveFormLayout>
    </>
  );
};

export default ScanIdScreen;
